import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Plus, X, Instagram, Facebook, Mail, MessageSquare } from "lucide-react";

/**
 * Floating contact actions — bottom-right speed-dial menu.
 * Clicking the primary button expands vertical buttons: WhatsApp, Instagram, Facebook, SMS, Email.
 */
export default function FloatingActions({ site }) {
  const [open, setOpen] = useState(false);

  // Build links from site config
  const digits = (site?.phone || "").replace(/\D/g, "");
  const waNumber = digits ? `1${digits.replace(/^1/, "")}` : "19787174044";
  const whatsappHref = `https://wa.me/${waNumber}?text=Hi%20M%26C%20Clean%20Fresh%2C%20I%27d%20like%20a%20free%20estimate.`;
  const smsHref = site?.phone_href ? site.phone_href.replace("tel:", "sms:") : `sms:+${waNumber}`;
  const emailHref = site?.email_href || `mailto:${site?.email || "service@mccleanfresh.com"}`;
  const instagramHref = site?.social?.instagram && site.social.instagram !== "#"
    ? site.social.instagram : "https://www.instagram.com/mccleanfresh";
  const facebookHref = site?.social?.facebook && site.social.facebook !== "#"
    ? site.social.facebook : "https://www.facebook.com/mccleanfresh";

  // Close on Esc
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const actions = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      href: whatsappHref,
      bg: "bg-[#25D366] hover:bg-[#1FBE5C]",
      icon: <WhatsAppIcon className="w-6 h-6 text-white" />,
      external: true,
    },
    {
      key: "instagram",
      label: "Instagram",
      href: instagramHref,
      bg: "bg-gradient-to-br from-[#FFD86B] via-[#E1306C] to-[#5B27B8] hover:opacity-90",
      icon: <Instagram className="w-5 h-5 text-white" strokeWidth={2.4} />,
      external: true,
    },
    {
      key: "facebook",
      label: "Facebook",
      href: facebookHref,
      bg: "bg-[#1877F2] hover:bg-[#1469DA]",
      icon: <Facebook className="w-5 h-5 text-white fill-white" />,
      external: true,
    },
    {
      key: "sms",
      label: "SMS",
      href: smsHref,
      bg: "bg-[#FF3D7F] hover:bg-[#EE2C6F]",
      icon: <MessageSquare className="w-5 h-5 text-white" strokeWidth={2.4} />,
      external: false,
    },
    {
      key: "email",
      label: "Email",
      href: emailHref,
      bg: "bg-[#4F66E5] hover:bg-[#3F55D8]",
      icon: <Mail className="w-5 h-5 text-white" strokeWidth={2.2} />,
      external: false,
    },
  ];

  return (
    <>
      <div
        data-testid="floating-actions"
        style={{ zIndex: 2147483646 }}
        // Sits above the mobile sticky bar on small screens, bottom-6 on desktop.
        className="fixed right-4 sm:right-6 bottom-24 lg:bottom-6 flex flex-col items-end gap-3"
      >
      <AnimatePresence>
        {open && (
          <motion.div
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={{
              hidden: { transition: { staggerChildren: 0.04, staggerDirection: -1 } },
              visible: { transition: { staggerChildren: 0.06, delayChildren: 0.02 } },
            }}
            className="flex flex-col items-end gap-3"
          >
            {actions.map((a) => (
              <motion.a
                key={a.key}
                href={a.href}
                target={a.external ? "_blank" : undefined}
                rel={a.external ? "noopener noreferrer" : undefined}
                aria-label={a.label}
                data-testid={`fab-${a.key}`}
                variants={{
                  hidden: { opacity: 0, y: 16, scale: 0.7 },
                  visible: { opacity: 1, y: 0, scale: 1 },
                }}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.92 }}
                transition={{ type: "spring", stiffness: 380, damping: 22 }}
                className={`group relative w-12 h-12 sm:w-13 sm:h-13 rounded-full ${a.bg} flex items-center justify-center shadow-[0_10px_30px_-8px_rgba(0,0,0,0.35)] transition-shadow hover:shadow-[0_14px_32px_-6px_rgba(0,0,0,0.45)]`}
                style={{ width: 52, height: 52 }}
              >
                {a.icon}
                <span className="pointer-events-none absolute right-full mr-3 px-3 py-1.5 rounded-full bg-[#0A0A0A] text-white text-[11px] font-medium tracking-wide uppercase opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-lg">
                  {a.label}
                </span>
              </motion.a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Primary toggle */}
      <motion.button
        onClick={() => setOpen((o) => !o)}
        whileTap={{ scale: 0.92 }}
        animate={{ rotate: open ? 135 : 0 }}
        transition={{ type: "spring", stiffness: 380, damping: 22 }}
        data-testid="fab-toggle"
        aria-label={open ? "Close contact menu" : "Open contact menu"}
        aria-expanded={open}
        className="relative w-14 h-14 rounded-full bg-gradient-to-br from-[#E6C75A] via-[#D4AF37] to-[#B8941F] text-white flex items-center justify-center shadow-[0_14px_34px_-8px_rgba(212,175,55,0.6)] hover:shadow-[0_18px_40px_-6px_rgba(212,175,55,0.8)] transition-shadow border border-[#D4AF37]/40"
      >
        {open ? <X className="w-6 h-6" strokeWidth={2.4} /> : <MessageCircle className="w-6 h-6 fill-white" />}
        {/* Pulse ring (only when closed) */}
        {!open && (
          <span className="absolute inset-0 rounded-full border-2 border-[#D4AF37] animate-ping opacity-40 pointer-events-none" />
        )}
      </motion.button>
    </div>
    </>
  );
}

/* Official WhatsApp glyph (lucide doesn't include it) */
function WhatsAppIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M19.05 4.91A9.82 9.82 0 0012.04 2c-5.45 0-9.88 4.43-9.88 9.88 0 1.74.45 3.43 1.32 4.92L2 22l5.34-1.4a9.88 9.88 0 004.7 1.19h.01c5.45 0 9.88-4.43 9.88-9.88 0-2.63-1.03-5.11-2.88-6.99zM12.05 20.2h-.01a8.2 8.2 0 01-4.18-1.14l-.3-.18-3.17.83.85-3.09-.2-.32a8.21 8.21 0 01-1.26-4.4c0-4.54 3.69-8.23 8.23-8.23 2.2 0 4.27.86 5.82 2.41a8.18 8.18 0 012.41 5.82c0 4.54-3.69 8.23-8.21 8.23zm4.51-6.16c-.25-.12-1.46-.72-1.69-.81-.23-.08-.39-.12-.56.12-.17.25-.65.81-.79.97-.15.17-.29.19-.54.06-.25-.12-1.04-.38-1.98-1.22-.73-.65-1.23-1.46-1.37-1.7-.14-.25-.02-.39.11-.51.11-.11.25-.29.38-.43.13-.15.17-.25.25-.42.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.57.12.17 1.75 2.67 4.24 3.74.59.26 1.06.41 1.42.52.6.19 1.14.16 1.57.1.48-.07 1.46-.6 1.67-1.18.21-.58.21-1.07.14-1.18-.06-.11-.23-.18-.48-.3z" />
    </svg>
  );
}
